import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Drawer from "@/components/admin/Drawer";
import StatusBadge, { STATUS_OPTIONS } from "@/components/admin/StatusBadge";
import { Loader2, Trash2, Eye, Search, Mail, Phone } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [statusEdit, setStatusEdit] = useState("new");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/contacts");
      setItems(res.data || []);
    } catch (e) { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(it => {
      if (statusFilter !== "all" && (it.status || "new") !== statusFilter) return false;
      if (!q) return true;
      return [it.name, it.email, it.subject, it.message, it.phone].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [items, query, statusFilter]);

  const openItem = (it) => {
    setSelected(it);
    setNotes(it.notes || "");
    setStatusEdit(it.status || "new");
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/contacts/${selected.id}`, { status: statusEdit, notes });
      toast.success("Updated");
      await load();
      setSelected(null);
    } catch (e) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Deleted");
      setSelected(null);
      await load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0F2A4A]">Contact Messages</h1>
          <p className="text-slate-600 text-sm">Messages submitted from the public contact form.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="hc-input pl-9 w-56" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="hc-input w-40">
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3780E0]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No messages found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#3780E0] text-white text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Contact</th>
                  <th className="px-4 py-3 font-semibold">Subject</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Received</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, idx) => (
                  <tr key={it.id} className={idx % 2 ? "bg-[#E8F0FB]" : "bg-white"}>
                    <td className="px-4 py-3 font-semibold text-[#0F2A4A]">{it.name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {it.email}</div>
                      {it.phone && <div className="flex items-center gap-1 text-xs"><Phone className="w-3.5 h-3.5" /> {it.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{it.subject || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openItem(it)} className="p-2 rounded hover:bg-slate-100" title="View" data-testid={`contact-view-${it.id}`}><Eye className="w-4 h-4 text-[#3780E0]" /></button>
                        <button onClick={() => del(it.id)} className="p-2 rounded hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={`Message from ${selected?.name || ""}`}
        footer={
          <div className="flex justify-between gap-3 items-center">
            <button onClick={() => del(selected?.id)} className="text-red-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:text-red-700"><Trash2 className="w-4 h-4" /> Delete</button>
            <button onClick={save} disabled={saving} className="hc-btn-primary disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
          </div>
        }
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs text-slate-500">Name</div><div className="font-semibold">{selected.name}</div></div>
              <div><div className="text-xs text-slate-500">Received</div><div>{formatDate(selected.created_at)}</div></div>
              <div><div className="text-xs text-slate-500">Email</div><a href={`mailto:${selected.email}`} className="hc-link">{selected.email}</a></div>
              <div><div className="text-xs text-slate-500">Phone</div>{selected.phone ? <a href={`tel:${selected.phone}`} className="hc-link">{selected.phone}</a> : <span className="text-slate-400">—</span>}</div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Subject</div><div>{selected.subject || <span className="text-slate-400">—</span>}</div></div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Message</div>
              <div className="p-3 bg-[#E8F0FB] rounded border border-[#C3D9F6] whitespace-pre-wrap">{selected.message}</div>
            </div>
            <div>
              <label className="hc-label">Status</label>
              <select value={statusEdit} onChange={(e) => setStatusEdit(e.target.value)} className="hc-input">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="hc-label">Internal notes</label>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="hc-input" placeholder="Notes visible only to admins…" />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
